import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { UserTable } from './UserTable';
import { CreateUserModal } from './CreateUserModal';
import { CompleteProfileModal } from './CompleteProfileModal';
import { EditUserModal } from './EditUserModal';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { useCountries } from '../hooks/useCountries';
import { FEATURES } from '@/config/appConfig';
import type { AdminUser, CreateUserRequest, CreateUserResponse, UpdateUserRequest, UserRole } from '../types/admin.types';

type AdminTab = 'users' | 'analytics';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCompleteProfileOpen, setIsCompleteProfileOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState<CreateUserResponse | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  const { countries } = useCountries();

  const {
    users,
    isLoading,
    error,
    clearError,
    isCreating,
    isUpdatingUser,
    editingUserId,
    togglingUserId,
    updatingRoleUserId,
    deletingUserId,
    toggleAuthError,
    updateRoleError,
    deleteError,
    editError,
    clearToggleAuthError,
    clearUpdateRoleError,
    clearDeleteError,
    clearEditError,
    loadUsers,
    createUser,
    updateUser,
    editUser,
    toggleAuthorization,
    updateRole,
    deleteUser,
  } = useAdminUsers();

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreate = async (data: CreateUserRequest) => {
    const result = await createUser(data);
    setCreatedUser(result);
    setIsCompleteProfileOpen(true);
    // CreateUserModal closes itself after onSubmit resolves
  };

  const handleCompleteProfile = async (data: UpdateUserRequest) => {
    if (!createdUser) return;
    await updateUser(createdUser.user_id, data);
    // CompleteProfileModal closes itself after onSubmit resolves → handleCompleteProfileClose reloads
  };

  const handleCompleteProfileClose = () => {
    setIsCompleteProfileOpen(false);
    setCreatedUser(null);
    loadUsers();
  };

  const handleEditUser = async (userId: number, data: UpdateUserRequest, newRol?: UserRole) => {
    await editUser(userId, data, newRol);
  };

  const handleEditClose = () => setEditingUser(null);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Top row */}
          <div className="flex items-center justify-between gap-4 py-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                aria-label="Back to chat"
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-lg font-semibold text-slate-800">Admin Panel</h1>
            </div>
            {activeTab === 'users' && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={loadUsers}
                  disabled={isLoading}
                  aria-label="Refresh users"
                  className="h-8 w-8"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  size="sm"
                  onClick={() => setIsCreateOpen(true)}
                  className="flex items-center gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  <span>New user</span>
                </Button>
              </div>
            )}
          </div>
          {/* Tab row */}
          <div className="flex gap-0.5 -mb-px">
            {(['users', ...(FEATURES.ANALYTICS_PANEL ? ['analytics'] : [])] as AdminTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize',
                  activeTab === tab
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                )}
              >
                {tab === 'users' ? 'Users' : 'Analytics'}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 px-4 py-6 md:px-6">
        <div className="max-w-5xl mx-auto">
          {activeTab === 'analytics' && FEATURES.ANALYTICS_PANEL ? (
            <AnalyticsDashboard />
          ) : isLoading && users.length === 0 ? (
            <div className="flex items-center justify-center py-20 gap-2 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading users…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-16">
              <AlertCircle className="h-8 w-8 text-red-400" />
              <p className="text-sm text-slate-600">{error}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearError}>
                  Back to list
                </Button>
                <Button variant="outline" size="sm" onClick={loadUsers}>
                  Try again
                </Button>
              </div>
            </div>
          ) : (
            <UserTable
              users={users}
              editingUserId={editingUserId}
              togglingUserId={togglingUserId}
              updatingRoleUserId={updatingRoleUserId}
              deletingUserId={deletingUserId}
              editError={editError}
              toggleAuthError={toggleAuthError}
              updateRoleError={updateRoleError}
              deleteError={deleteError}
              onClearEditError={clearEditError}
              onClearToggleAuthError={clearToggleAuthError}
              onClearUpdateRoleError={clearUpdateRoleError}
              onClearDeleteError={clearDeleteError}
              onEditUser={(user) => setEditingUser(user)}
              onToggleAuthorization={toggleAuthorization}
              onUpdateRole={updateRole}
              onDeleteUser={deleteUser}
            />
          )}
        </div>
      </main>

      <CreateUserModal
        isOpen={isCreateOpen}
        isLoading={isCreating}
        onSubmit={handleCreate}
        onClose={() => setIsCreateOpen(false)}
      />

      <CompleteProfileModal
        isOpen={isCompleteProfileOpen}
        isLoading={isUpdatingUser}
        userName={createdUser?.name || createdUser?.email || ''}
        countries={countries}
        onSubmit={handleCompleteProfile}
        onClose={handleCompleteProfileClose}
      />

      <EditUserModal
        user={editingUser}
        isLoading={!!editingUserId}
        isSuperAdmin={isSuperAdmin}
        countries={countries}
        onSubmit={handleEditUser}
        onClose={handleEditClose}
      />
    </div>
  );
};
